import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { Logo } from "@/components/brand/Logo";
import { useAuditBooking } from "@/components/layout/AuditBookingContext";
import { pushAuditStart } from "@/lib/analytics";
import googlePartnerLogo from "@/assets/google-partner-logo-transparent.png";
import { automationLinks } from "@/content/aiAutomation";


type PanelItem = { to: string; label: string; description?: string };
const panels = [
  { label: "Services", to: "/services", heading: "Paid media", bottom: "All services →", bottomTo: "/services", items: [
    { to: "/services/google-ads", label: "Google Ads", description: "Search, Performance Max, Local" },
    { to: "/services/meta-ads", label: "Meta Ads", description: "Facebook and Instagram" },
    { to: "/services/local-services-ads", label: "Local Services Ads", description: "Google Verified leads" },
    { to: "/services/youtube-ads", label: "YouTube Ads", description: "In-stream and Shorts" },
    { to: "/services/seo-ai-search", label: "SEO & AI Search" },
  ] },
  { label: "Industries", to: "/industries", bottom: "All industries →", bottomTo: "/industries", items: [
    ["Roofing", "roofing"], ["Plumbing", "plumbing"], ["HVAC", "hvac"], ["Electricians", "electricians"],
    ["Landscaping", "landscaping"], ["Pool service", "pool-service"], ["Painting", "painting"], ["Pest control", "pest-control"],
    ["Remodeling", "remodeling"], ["Concrete", "concrete"], ["Windows", "window-replacement"], ["Cleaning", "cleaning"], ["Tree services", "tree-services"],
  ].map(([label, slug]) => ({ label: label ?? "", to: `/industries/${slug}` })) },
  { label: "Resources", to: "/research", bottom: "Free audit →", bottomTo: "/audit", items: [
    { to: "/research/conversion-tracking-report", label: "Tracking report", description: "9,560 sites, loaded in a browser" },
    { to: "/blog", label: "Blog", description: "Guides for contractors" },
    { to: "/glossary", label: "Glossary", description: "Plain-English definitions" },
    { to: "/about", label: "About", description: "Based in Scottsdale, AZ" },
    { to: "/ai", label: "Company facts", description: "Who we are, in one page" },
  ] },
];
function PanelContent({ panel, closeMenu }: { panel: typeof panels[number]; closeMenu?: () => void }) {
  return <>
    <div className={panel.label === "Services" ? "nav-service-columns" : ""}>
    <div>
    {panel.heading && <div className="nav-panel-heading mono">{panel.heading}</div>}
    <div className={`nav-panel-links ${panel.label === "Industries" ? "nav-industries" : ""}`}>
      {(panel.items as PanelItem[]).map(item => <Link key={item.to} to={item.to} onClick={closeMenu} className="nav-panel-link">
        <span>{item.label}</span>{item.description && <small>{item.description}</small>}
      </Link>)}
    </div>
    </div>
    {panel.label === "Services" && <div>
      <div className="nav-panel-heading mono">AI &amp; Automation</div>
      <div className="nav-panel-links">{automationLinks.map(item => <Link key={item.to} to={item.to} onClick={closeMenu} className="nav-panel-link"><span>{item.label}</span><small>{item.description}</small></Link>)}</div>
    </div>}
    </div>
    <Link to={panel.bottomTo} onClick={closeMenu} className="nav-panel-bottom mono">{panel.bottom}</Link>
  </>;
}
function DesktopPanel({ label }: { label: string }) {
  const panel = panels.find(item => item.label === label);
  if (!panel) return null;
  return <div className={`nav-panel-group nav-panel-${label.toLowerCase()}`}>
    <Link to={panel.to} className="nav-top-link">{panel.label}<svg className="nav-chevron" viewBox="0 0 12 12" aria-hidden="true"><path d="m3 4.5 3 3 3-3" /></svg></Link>
    <div className="nav-panel"><PanelContent panel={panel} /></div>
  </div>;
}
function MobilePanel({ label, closeMenu }: { label: string; closeMenu: () => void }) {
  const panel = panels.find(item => item.label === label);
  if (!panel) return null;
  return <details className="mobile-nav-section">
    <summary className="mono">{label}<span aria-hidden="true">⌄</span></summary>
    <div className="mobile-nav-panel"><PanelContent panel={panel} closeMenu={closeMenu} /></div>
  </details>;
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { location } = useRouterState();
  const { hasResult, openBooking } = useAuditBooking();
  const isAuditPage = ["/audit", "/audit/", "/audit/e", "/audit/e/"].includes(location.pathname);

  const showBooking = isAuditPage && hasResult;

  return (
    <nav
      className="sticky top-0 z-50 border-b"
      style={{
        background: "rgba(251,250,246,0.85)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderColor: "rgba(0,47,167,0.12)",
      }}
    >
      <div
        className="mx-auto flex items-center justify-between gap-6"
        style={{ maxWidth: 1280, padding: "14px 36px", minHeight: 64 }}
      >
        <div className="flex items-center gap-3 md:gap-4">
          <Logo variant="bleu" className="h-[30px] md:h-10 w-auto" />
          <div className="hidden lg:flex items-center gap-3 md:gap-4">
            <span
              aria-hidden="true"
              style={{
                width: 1,
                height: 32,
                background: "rgba(0,47,167,0.16)",
                flexShrink: 0,
              }}
            />
            <img
              src={googlePartnerLogo}
              alt="Google Partner"
              className="google-badge-hover"
              width={426}
              height={295}
              loading="lazy"
              decoding="async"
              style={{ height: 34, width: "auto", objectFit: "contain", display: "block" }}
            />
          </div>
        </div>

        <div className="desktop-nav mono">
          <DesktopPanel label="Services" />
          <DesktopPanel label="Industries" />
          <Link to="/case-studies" className="nav-top-link">Results</Link>
          <DesktopPanel label="Resources" />
          <Link to="/contact" className="nav-top-link">Contact</Link>
        </div>

        <div className="flex items-center gap-2">
          {showBooking ? (
            <button
              type="button"
              onClick={openBooking}
              className="bhg-btn-primary header-audit-cta hidden lg:inline-flex"
            >
              Book your 30-min review →
            </button>
          ) : (
            <Link
              to="/audit"
              onClick={() => pushAuditStart("header")}
              className="bhg-btn-primary header-audit-cta hidden lg:inline-flex"
            >
              Get your free audit →
            </Link>
          )}

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label="Open menu"
            aria-expanded={open}
            className="flex h-9 w-9 items-center justify-center rounded-[4px] border lg:hidden"
            style={{ borderColor: "rgba(0,47,167,0.26)" }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#002FA7"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-[18px] w-[18px]"
            >
              {open ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </>
              ) : (
                <>
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="17" x2="20" y2="17" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      <div hidden={!open} className={open ? "" : "hidden"}>
        <div
          className="border-t lg:hidden"
          style={{
            background: "rgba(251,250,246,0.97)",
            backdropFilter: "blur(14px)",
            borderColor: "rgba(0,47,167,0.26)",
          }}
        >
          <MobilePanel label="Services" closeMenu={() => setOpen(false)} />
          <MobilePanel label="Industries" closeMenu={() => setOpen(false)} />
          <Link to="/case-studies" onClick={() => setOpen(false)} className="mobile-nav-row mono">Results</Link>
          <MobilePanel label="Resources" closeMenu={() => setOpen(false)} />
          <Link to="/contact" onClick={() => setOpen(false)} className="mobile-nav-row mono">Contact</Link>
          <div className="px-9 py-5">
            {showBooking ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  openBooking();
                }}
                className="bhg-btn-primary w-full justify-center"
              >
                Book your 30-min review →
              </button>
            ) : (
              <Link
                to="/audit"
                onClick={() => {
                  setOpen(false);
                  pushAuditStart("header");
                }}
                className="bhg-btn-primary w-full justify-center"
              >
                Get your free audit →
              </Link>
            )}
          </div>

        </div>
      </div>

      <style>{`
        .bhg-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 4px;
          font-family: "IBM Plex Mono", "IBM Plex Mono Fallback", monospace;
          font-size: 11.5px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: #8a6f3f;
          color: #ffffff;
          border: 1px solid transparent;
          transition: transform .15s ease, background .15s ease;
          line-height: 1;
          cursor: pointer;
        }
        .bhg-btn-primary:hover {
          background: #6f5829;
          transform: translateY(-1px);
        }
        .google-badge-hover {
          transition: transform .2s ease, filter .2s ease;
          will-change: transform, filter;
        }
        .google-badge-hover:hover {
          transform: scale(1.06);
          filter: drop-shadow(0 0 5px rgba(66,133,244,0.35))
                  drop-shadow(0 0 10px rgba(234,67,53,0.22))
                  drop-shadow(0 0 15px rgba(251,188,5,0.18))
                  drop-shadow(0 0 20px rgba(52,168,83,0.15));
        }
      `}</style>
    </nav>
  );
}
